
import { AuthViewModel, SignInLockout, type StaffSessionUser } from "@lumora/core";
import { useViewModel } from "@lumora/ui";
import { useRef, useEffect, useMemo } from "react";
import Icons from '../component/Icons';
import { SITE, client } from "../../client";
import { resolveStaffLoginEmail } from "../../lib/staffIdentity";
import { staffSession } from "../../lib/vms";

const browserStorage = () => {
    try {
        return typeof localStorage === "undefined" ? null : localStorage;
    } catch {
        return null;
    }
};

const AdminLoginView = ({ onAuthenticated, navigate }: { onAuthenticated: (user: StaffSessionUser) => void; navigate: (path: string) => void }) => {
    const viewModel = useMemo(() => new AuthViewModel(client, SITE, {
        minIdentifierLength: 3,
        minPasswordLength: 12,
        minNewPasswordLength: 8,
        lockout: new SignInLockout({ storage: browserStorage(), key: 'admin_lockout_until' }),
        resolveIdentifier: resolveStaffLoginEmail,
        resetPort: {
            reset: async () => {
                throw new Error('Password reset is managed by Lumora admin invite.');
            },
        },
        messages: {
            credentialsHint: 'Enter your store username and password (12+ characters).',
            signInFailed: 'Invalid username or password.',
        },
    }), []);
    const state = useViewModel(viewModel);
    const username = state.email;
    const password = state.password;
    const resetCode = state.resetCode;
    const newPassword = state.newPassword;
    const isResetMode = state.mode === 'reset';
    const isLoading = state.loading;
    const error = state.error ?? '';
    const timeRemaining = state.lockedSeconds;
    const isLocked = timeRemaining > 0;

    const userRef = useRef<HTMLInputElement>(null);

    // Handle Countdown Timer
    useEffect(() => {
        if (!isLocked) return;
        const interval = setInterval(() => viewModel.refreshLockout(), 1000);
        return () => clearInterval(interval);
    }, [isLocked, viewModel]);

    useEffect(() => {
        if (!isLocked) userRef.current?.focus();
    }, [isLocked]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (await viewModel.signIn()) {
            const user = staffSession.signedIn(viewModel.state.email);
            if (user) onAuthenticated(user);
        }
    };

    const handlePasswordReset = async (e: React.FormEvent) => {
        e.preventDefault();
        await viewModel.resetPassword();
    };

    // Format remaining time nicely
    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    return (
        <div className="flex-grow flex items-center justify-center bg-gray-100 p-4">
            <div className="max-w-md w-full bg-white p-8 md:p-10 rounded-[2rem] shadow-2xl border border-gray-200 relative overflow-hidden">
                {/* Security Banner */}
                <div className="absolute top-0 left-0 right-0 bg-red-600 text-white text-[10px] font-black uppercase tracking-[0.3em] py-1.5 text-center flex justify-center items-center gap-2">
                    <Icons.Shield /> Staff Portal
                </div>

                <div className="text-center mt-4 mb-8">
                    <h2 className="text-3xl font-black italic tracking-tighter uppercase text-gray-900">Sign In</h2>
                    <p className="text-gray-400 font-bold text-xs uppercase tracking-widest mt-1">Authorized Personnel Only</p>
                </div>

                <form onSubmit={isResetMode ? handlePasswordReset : handleLogin} className="space-y-5" autoComplete="off">
                    <div>
                        <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Username</label>
                        <input 
                            ref={userRef}
                            type="text" 
                            required 
                            disabled={isLocked}
                            autoComplete="off"
                            spellCheck="false"
                            className={`w-full border-2 border-gray-100 rounded-xl p-3 focus:ring-0 outline-none transition font-medium ${isLocked ? 'bg-gray-200 text-gray-400' : 'bg-gray-50 focus:bg-white focus:border-gray-900'}`} 
                            value={username} 
                            onChange={e => viewModel.setEmail(e.target.value)} 
                        />
                    </div>
                    {!isResetMode ? (
                        <div>
                            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Password</label>
                            <input 
                                type="password" 
                                required 
                                disabled={isLocked}
                                autoComplete="new-password"
                                className={`w-full border-2 border-gray-100 rounded-xl p-3 focus:ring-0 outline-none transition font-medium ${isLocked ? 'bg-gray-200 text-gray-400' : 'bg-gray-50 focus:bg-white focus:border-gray-900'}`} 
                                value={password} 
                                onChange={e => viewModel.setPassword(e.target.value)} 
                            />
                        </div>
                    ) : (
                        <>
                            <div>
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">One-Time Reset Code</label>
                                <input 
                                    type="text" 
                                    required 
                                    disabled={isLocked}
                                    autoComplete="one-time-code"
                                    className={`w-full border-2 border-gray-100 rounded-xl p-3 focus:ring-0 outline-none transition font-medium ${isLocked ? 'bg-gray-200 text-gray-400' : 'bg-gray-50 focus:bg-white focus:border-gray-900'}`} 
                                    value={resetCode} 
                                    onChange={e => viewModel.setResetCode(e.target.value)} 
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">New Password</label>
                                <input 
                                    type="password" 
                                    required 
                                    disabled={isLocked}
                                    autoComplete="new-password"
                                    className={`w-full border-2 border-gray-100 rounded-xl p-3 focus:ring-0 outline-none transition font-medium ${isLocked ? 'bg-gray-200 text-gray-400' : 'bg-gray-50 focus:bg-white focus:border-gray-900'}`} 
                                    value={newPassword} 
                                    onChange={e => viewModel.setNewPassword(e.target.value)} 
                                />
                            </div>
                        </>
                    )}
                    
                    {(error || isLocked) && (
                        <div className="bg-red-50 text-red-600 border border-red-100 rounded-lg p-3 text-xs font-bold flex items-start gap-2 animate-in fade-in slide-in-from-top-2">
                            <div className="mt-0.5 flex-shrink-0"><Icons.Shield /></div>
                            <div>
                                {isLocked ? `Maximum attempts reached. For your security, this device has been locked out for ${formatTime(timeRemaining)}.` : error}
                            </div>
                        </div>
                    )}

                    <button 
                        type="submit" 
                        disabled={isLoading || isLocked}
                        className={`w-full text-white font-black uppercase tracking-widest py-4 rounded-xl transition shadow-xl mt-2 ${
                            isLocked ? 'bg-red-800 opacity-50 cursor-not-allowed' : 
                            isLoading ? 'bg-gray-900 opacity-70 cursor-wait' : 'bg-gray-900 hover:bg-black transform hover:-translate-y-1'
                        }`}
                    >
                        {isLocked ? `Locked (${formatTime(timeRemaining)})` : isLoading ? 'Authenticating...' : isResetMode ? 'Set Password' : 'Secure Login'}
                    </button>
                </form>

                <div className="mt-8 text-center border-t border-gray-100 pt-6">
                    <button onClick={() => {
                        viewModel.setMode(isResetMode ? 'signIn' : 'reset');
                        viewModel.setPassword('');
                        viewModel.setResetCode('');
                        viewModel.setNewPassword('');
                    }} className="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-900 transition flex items-center justify-center gap-2 mx-auto mb-4">
                        {isResetMode ? 'Return to password login' : 'Use one-time reset code'}
                    </button>
                    <button onClick={() => navigate('home')} className="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-red-600 transition flex items-center justify-center gap-2 mx-auto">
                        <span>&larr;</span> Back to Public Website
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminLoginView;
